import React from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Popover,
  Button,
  Checkbox,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PostSummary,
  createPost,
  listPostsSummary,
  updatePost,
  updatePostStatus,
} from '../api/posts';
import { deleteDocsInCollection } from '../api/batchDelete';
import { listSchoolsSummary } from '../api/schools';
import { useAuth } from '../auth/AuthContext';
import { downloadRecordsCsv } from '../utils/csvExport';
import {
  EntityAutocomplete,
  type EntityOption,
} from '../components/EntityAutocomplete';

const formatDate = (date?: Date) =>
  date ? date.toLocaleDateString() : '—';

const statusColor = (status?: string) => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    case 'pending':
    default:
      return 'warning';
  }
};

function dateToInputValue(d?: Date): string {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function inputValueToDate(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
}

function PostTextPreviewCell({ text }: { text?: string }) {
  const raw = (text ?? '').trim();
  const display = raw || '—';
  return (
    <TableCell sx={{ maxWidth: 260, verticalAlign: 'top' }}>
      <Tooltip title={raw} disableHoverListener={!raw} placement="top-start">
        <Typography
          variant="body2"
          color={raw ? 'text.primary' : 'text.secondary'}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {display}
        </Typography>
      </Tooltip>
    </TableCell>
  );
}

export const PostsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const canEdit = role === 'admin';
  const { data, isLoading } = useQuery<PostSummary[]>({
    queryKey: ['posts'],
    queryFn: listPostsSummary,
  });
  const { data: schools } = useQuery({
    queryKey: ['schools-summary'],
    queryFn: listSchoolsSummary,
  });

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const [orderBy, setOrderBy] = React.useState<
    'title' | 'school' | 'status' | 'date' | 'createdAt' | 'updatedAt'
  >('date');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('desc');

  const [confirmAnchorEl, setConfirmAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const [confirmDescription, setConfirmDescription] = React.useState('');
  const [pendingPost, setPendingPost] = React.useState<{
    ids: string[];
    status: string;
  } | null>(null);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const [editPost, setEditPost] = React.useState<PostSummary | null>(null);
  const [editTitle, setEditTitle] = React.useState('');
  const [editSchoolId, setEditSchoolId] = React.useState('');
  const [editPostStatus, setEditPostStatus] = React.useState('pending');
  const [editPostDate, setEditPostDate] = React.useState('');
  const [editSummary, setEditSummary] = React.useState('');
  const [editDescription, setEditDescription] = React.useState('');
  const [editSaving, setEditSaving] = React.useState(false);
  const [editError, setEditError] = React.useState<string | null>(null);

  const [createOpen, setCreateOpen] = React.useState(false);
  const [createTitle, setCreateTitle] = React.useState('');
  const [createSchoolId, setCreateSchoolId] = React.useState('');
  const [createPostStatus, setCreatePostStatus] = React.useState('pending');
  const [createPostDate, setCreatePostDate] = React.useState('');
  const [createSummary, setCreateSummary] = React.useState('');
  const [createDescription, setCreateDescription] = React.useState('');
  const [createSaving, setCreateSaving] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);

  const confirmOpen = Boolean(confirmAnchorEl);

  const schoolNameById = React.useMemo(() => {
    const map: Record<string, string> = {};
    schools?.forEach((s) => {
      map[s.id] = s.name || s.id;
    });
    return map;
  }, [schools]);

  const schoolEditOptions = React.useMemo<EntityOption[]>(() => {
    if (!schools?.length) return [];
    return [...schools]
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'fr', { sensitivity: 'base' }))
      .map((s) => ({
        id: s.id,
        label:
          [s.name, s.commune, s.region, s.academy].filter(Boolean).join(' · ') || 'École',
      }));
  }, [schools]);

  const handleRequestSort = (
    property:
      | 'title'
      | 'school'
      | 'status'
      | 'date'
      | 'createdAt'
      | 'updatedAt'
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filtered = React.useMemo(() => {
    if (!data) return [];
    return data.filter((p) => {
      const schoolName = p.schoolId
        ? schoolNameById[p.schoolId] ?? p.schoolId
        : '';
      const haystack = `${p.title ?? ''} ${p.summary ?? ''} ${p.description ?? ''} ${schoolName}`
        .toLowerCase();
      const matchesSearch = haystack.includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' || (p.status ?? 'pending') === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [data, search, statusFilter, schoolNameById]);

  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const factor = order === 'asc' ? 1 : -1;
      let av: string | Date | undefined;
      let bv: string | Date | undefined;

      const getSchoolName = (p: PostSummary) =>
        p.schoolId ? schoolNameById[p.schoolId] ?? p.schoolId : '';

      switch (orderBy) {
        case 'title':
          av = (a.title ?? '').toLowerCase();
          bv = (b.title ?? '').toLowerCase();
          break;
        case 'school':
          av = getSchoolName(a).toLowerCase();
          bv = getSchoolName(b).toLowerCase();
          break;
        case 'status':
          av = (a.status ?? '').toLowerCase();
          bv = (b.status ?? '').toLowerCase();
          break;
        case 'date':
          av = a.date ?? new Date(0);
          bv = b.date ?? new Date(0);
          break;
        case 'createdAt':
          av = a.createdAt ?? new Date(0);
          bv = b.createdAt ?? new Date(0);
          break;
        case 'updatedAt':
          av = a.updatedAt ?? new Date(0);
          bv = b.updatedAt ?? new Date(0);
          break;
        default:
          av = 0 as any;
          bv = 0 as any;
      }

      if (av instanceof Date && bv instanceof Date) {
        return (av.getTime() - bv.getTime()) * factor;
      }

      const as = String(av);
      const bs = String(bv);
      if (as < bs) return -1 * factor;
      if (as > bs) return 1 * factor;
      return 0;
    });
    return arr;
  }, [filtered, order, orderBy, schoolNameById]);

  const paginated = React.useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleChangeStatusRequest = (
    event: React.MouseEvent<HTMLElement>,
    idsOrPost: PostSummary | string[],
    status: string
  ) => {
    const actionLabel = status === 'approved' ? 'approuver' : 'rejeter';

    if (Array.isArray(idsOrPost)) {
      const ids = idsOrPost;
      if (!ids.length) return;
      setConfirmDescription(
        `Voulez-vous vraiment ${actionLabel} les ${ids.length} postes sélectionnés ?`
      );
      setPendingPost({ ids, status });
    } else {
      const post = idsOrPost;
      const schoolName = post.schoolId
        ? schoolNameById[post.schoolId] ?? post.schoolId
        : 'cette école';
      setConfirmDescription(
        `Voulez-vous vraiment ${actionLabel} le poste "${post.title ?? 'Sans titre'}" pour ${schoolName} ?`
      );
      setPendingPost({ ids: [post.id], status });
    }

    setConfirmAnchorEl(event.currentTarget);
  };

  const handleConfirmPost = async () => {
    if (!pendingPost) return;
    await Promise.all(
      pendingPost.ids.map((id) => updatePostStatus(id, pendingPost.status))
    );
    await queryClient.invalidateQueries({ queryKey: ['posts'] });
    setConfirmAnchorEl(null);
    setPendingPost(null);
    setSelectedIds([]);
  };

  const handleCancelPost = () => {
    setConfirmAnchorEl(null);
    setPendingPost(null);
  };

  const handleConfirmDeleteSelected = async () => {
    if (!selectedIds.length) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteDocsInCollection('posts', selectedIds);
      await queryClient.invalidateQueries({ queryKey: ['posts'] });
      setSelectedIds([]);
      setDeleteDialogOpen(false);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : "La suppression a échoué.");
    } finally {
      setDeleting(false);
    }
  };

  const allSelected =
    filtered.length > 0 &&
    filtered.every((p) => selectedIds.includes(p.id));
  const someSelected =
    filtered.length > 0 &&
    filtered.some((p) => selectedIds.includes(p.id)) &&
    !allSelected;

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(filtered.map((p) => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const openEditPost = (p: PostSummary) => {
    setEditPost(p);
    setEditTitle(p.title ?? '');
    setEditSchoolId(p.schoolId ?? '');
    setEditPostStatus(p.status ?? 'pending');
    setEditPostDate(dateToInputValue(p.date));
    setEditSummary(p.summary ?? '');
    setEditDescription(p.description ?? '');
    setEditError(null);
  };

  const closeEditPost = () => {
    if (editSaving) return;
    setEditPost(null);
    setEditError(null);
  };

  const openCreatePost = () => {
    setEditPost(null);
    setEditError(null);
    setCreateTitle('');
    setCreateSchoolId('');
    setCreatePostStatus('pending');
    setCreatePostDate(dateToInputValue(new Date()));
    setCreateSummary('');
    setCreateDescription('');
    setCreateError(null);
    setCreateOpen(true);
  };

  const closeCreatePost = () => {
    if (createSaving) return;
    setCreateOpen(false);
    setCreateError(null);
  };

  const handleSaveCreatePost = async () => {
    if (!createTitle.trim()) {
      setCreateError('Le titre est obligatoire.');
      return;
    }
    if (!createSchoolId.trim()) {
      setCreateError("L'école est obligatoire.");
      return;
    }
    const d = inputValueToDate(createPostDate);
    if (!d) {
      setCreateError('Date invalide (AAAA-MM-JJ).');
      return;
    }
    if (!['pending', 'approved', 'rejected'].includes(createPostStatus)) {
      setCreateError('Statut invalide.');
      return;
    }
    setCreateSaving(true);
    setCreateError(null);
    try {
      await createPost({
        title: createTitle.trim(),
        schoolId: createSchoolId.trim(),
        status: createPostStatus,
        date: d,
        summary: createSummary,
        description: createDescription,
      });
      await queryClient.invalidateQueries({ queryKey: ['posts'] });
      setCreateOpen(false);
      setPage(0);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Création impossible.');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleSavePost = async () => {
    if (!editPost) return;
    if (!editTitle.trim()) {
      setEditError('Le titre est obligatoire.');
      return;
    }
    if (!editSchoolId.trim()) {
      setEditError("L'école (schoolId) est obligatoire.");
      return;
    }
    const d = inputValueToDate(editPostDate);
    if (!d) {
      setEditError('Date invalide (AAAA-MM-JJ).');
      return;
    }
    if (!['pending', 'approved', 'rejected'].includes(editPostStatus)) {
      setEditError('Statut invalide.');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await updatePost(editPost.id, {
        title: editTitle.trim(),
        schoolId: editSchoolId.trim(),
        status: editPostStatus,
        date: d,
        summary: editSummary,
        description: editDescription,
      });
      await queryClient.invalidateQueries({ queryKey: ['posts'] });
      setEditPost(null);
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleExportCsv = () => {
    const rows: Array<Record<string, unknown>> = sorted.map((p) => {
      const sid = p.schoolId ?? '';
      return {
        postId: p.id,
        title: p.title ?? '',
        summary: p.summary ?? '',
        description: p.description ?? '',
        schoolId: sid,
        schoolName: sid ? schoolNameById[sid] ?? '' : '',
        status: p.status ?? '',
        date: p.date,
        createdAt: p.createdAt,
        updatedAt: p.updatedAt,
      };
    });
    downloadRecordsCsv('postes', rows);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Postes
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1,
        }}
      >
        {canEdit && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreatePost}>
            Ajouter
          </Button>
        )}
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          disabled={!sorted.length}
          onClick={handleExportCsv}
        >
          Exporter CSV
        </Button>
      </Box>
      {canEdit && (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <TextField
          size="small"
          label="Rechercher"
          placeholder="Titre, résumé, description, école..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="status-filter-label">Statut</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Statut"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="pending">En attente</MenuItem>
            <MenuItem value="approved">Approuvé</MenuItem>
            <MenuItem value="rejected">Rejeté</MenuItem>
          </Select>
        </FormControl>
      </Box>
      )}
      {canEdit && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {selectedIds.length
            ? `${selectedIds.length} sélectionné(s)`
            : 'Aucune sélection'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            disabled={!selectedIds.length}
            onClick={(event) =>
              handleChangeStatusRequest(event, selectedIds, 'approved')
            }
          >
            Approuver la sélection
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={!selectedIds.length}
            onClick={(event) =>
              handleChangeStatusRequest(event, selectedIds, 'rejected')
            }
          >
            Rejeter la sélection
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={!selectedIds.length}
            onClick={() => {
              setDeleteError(null);
              setDeleteDialogOpen(true);
            }}
          >
            Supprimer la sélection
          </Button>
        </Box>
      </Box>
      )}
      {deleteError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setDeleteError(null)}>
          {deleteError}
        </Alert>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {canEdit && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                  />
                </TableCell>
              )}
              <TableCell sortDirection={orderBy === 'title' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'title'}
                  direction={orderBy === 'title' ? order : 'asc'}
                  onClick={() => handleRequestSort('title')}
                >
                  Titre
                </TableSortLabel>
              </TableCell>
              <TableCell>Résumé</TableCell>
              <TableCell>Description</TableCell>
              <TableCell sortDirection={orderBy === 'school' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'school'}
                  direction={orderBy === 'school' ? order : 'asc'}
                  onClick={() => handleRequestSort('school')}
                >
                  École
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Statut
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'date' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'date'}
                  direction={orderBy === 'date' ? order : 'asc'}
                  onClick={() => handleRequestSort('date')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'createdAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('createdAt')}
                >
                  Créé le
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'updatedAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'updatedAt'}
                  direction={orderBy === 'updatedAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('updatedAt')}
                >
                  Mis à jour le
                </TableSortLabel>
              </TableCell>
              {canEdit && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={canEdit ? 10 : 8}>Chargement...</TableCell>
              </TableRow>
            )}
            {!isLoading &&
              paginated.map((p) => (
                <TableRow key={p.id} hover selected={canEdit && selectedIds.includes(p.id)}>
                  {canEdit && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedIds.includes(p.id)}
                        onChange={() => toggleOne(p.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell>{p.title ?? 'Sans titre'}</TableCell>
                  <PostTextPreviewCell text={p.summary} />
                  <PostTextPreviewCell text={p.description} />
                  <TableCell>
                    {p.schoolId
                      ? schoolNameById[p.schoolId] ?? p.schoolId
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={p.status ?? 'pending'}
                      color={statusColor(p.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(p.date)}</TableCell>
                  <TableCell>{formatDate(p.createdAt)}</TableCell>
                  <TableCell>{formatDate(p.updatedAt)}</TableCell>
                  {canEdit && (
                    <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          flexWrap: 'nowrap',
                          gap: 0.25,
                        }}
                      >
                        <Tooltip title="Modifier le poste">
                          <IconButton size="small" onClick={() => openEditPost(p)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {p.status !== 'approved' && (
                          <Tooltip title="Approuver le poste">
                            <IconButton
                              size="small"
                              onClick={(event) =>
                                handleChangeStatusRequest(event, p, 'approved')
                              }
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {p.status !== 'rejected' && (
                          <Tooltip title="Rejeter le poste">
                            <IconButton
                              size="small"
                              onClick={(event) =>
                                handleChangeStatusRequest(event, p, 'rejected')
                              }
                            >
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            {!isLoading && !filtered.length && (
              <TableRow>
                <TableCell colSpan={canEdit ? 10 : 8}>
                  <Typography variant="body2" color="text.secondary">
                    Aucun poste trouvé.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100, 500]}
      />
      <Dialog open={createOpen} onClose={closeCreatePost} maxWidth="md" fullWidth>
        <DialogTitle>Nouveau poste</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {createError && (
            <Alert severity="error" onClose={() => setCreateError(null)}>
              {createError}
            </Alert>
          )}
          <TextField
            label="Titre"
            required
            value={createTitle}
            onChange={(e) => setCreateTitle(e.target.value)}
            disabled={createSaving}
          />
          <TextField
            label="Résumé (summary)"
            value={createSummary}
            onChange={(e) => setCreateSummary(e.target.value)}
            disabled={createSaving}
            multiline
            minRows={2}
          />
          <TextField
            label="Description"
            value={createDescription}
            onChange={(e) => setCreateDescription(e.target.value)}
            disabled={createSaving}
            multiline
            minRows={4}
          />
          <EntityAutocomplete
            label="École"
            required
            options={schoolEditOptions}
            valueId={createSchoolId}
            onChangeId={setCreateSchoolId}
            disabled={createSaving}
            loading={!schools}
            helperText="Rechercher par nom, commune, région ou académie"
            orphanLabel={createSchoolId.trim() ? 'École (hors liste chargée)' : undefined}
          />
          <TextField
            label="Date"
            type="date"
            value={createPostDate}
            onChange={(e) => setCreatePostDate(e.target.value)}
            disabled={createSaving}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small">
            <InputLabel id="create-post-status-label">Statut</InputLabel>
            <Select
              labelId="create-post-status-label"
              label="Statut"
              value={createPostStatus}
              onChange={(e) => setCreatePostStatus(e.target.value)}
              disabled={createSaving}
            >
              <MenuItem value="pending">En attente</MenuItem>
              <MenuItem value="approved">Approuvé</MenuItem>
              <MenuItem value="rejected">Rejeté</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreatePost} color="inherit" disabled={createSaving}>
            Annuler
          </Button>
          <Button
            onClick={() => void handleSaveCreatePost()}
            variant="contained"
            disabled={createSaving}
          >
            {createSaving ? 'Création…' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editPost)} onClose={closeEditPost} maxWidth="md" fullWidth>
        <DialogTitle>Modifier le poste</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {editError && (
            <Alert severity="error" onClose={() => setEditError(null)}>
              {editError}
            </Alert>
          )}
          <TextField
            label="Titre"
            required
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            disabled={editSaving}
          />
          <TextField
            label="Résumé (summary)"
            value={editSummary}
            onChange={(e) => setEditSummary(e.target.value)}
            disabled={editSaving}
            multiline
            minRows={2}
          />
          <TextField
            label="Description"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            disabled={editSaving}
            multiline
            minRows={4}
          />
          <EntityAutocomplete
            label="École"
            required
            options={schoolEditOptions}
            valueId={editSchoolId}
            onChangeId={setEditSchoolId}
            disabled={editSaving}
            loading={!schools}
            helperText="Rechercher par nom, commune, région ou académie"
            orphanLabel={editSchoolId.trim() ? 'École (hors liste chargée)' : undefined}
          />
          <TextField
            label="Date"
            type="date"
            value={editPostDate}
            onChange={(e) => setEditPostDate(e.target.value)}
            disabled={editSaving}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small">
            <InputLabel id="edit-post-status-label">Statut</InputLabel>
            <Select
              labelId="edit-post-status-label"
              label="Statut"
              value={editPostStatus}
              onChange={(e) => setEditPostStatus(e.target.value)}
              disabled={editSaving}
            >
              <MenuItem value="pending">En attente</MenuItem>
              <MenuItem value="approved">Approuvé</MenuItem>
              <MenuItem value="rejected">Rejeté</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditPost} color="inherit" disabled={editSaving}>
            Annuler
          </Button>
          <Button onClick={() => void handleSavePost()} variant="contained" disabled={editSaving}>
            {editSaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={canEdit && confirmOpen}
        anchorEl={confirmAnchorEl}
        onClose={handleCancelPost}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Typography variant="subtitle2" gutterBottom>
            Confirmer l&apos;action
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {confirmDescription}
          </Typography>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button onClick={handleCancelPost} color="inherit" size="small">
              Annuler
            </Button>
            <Button
              onClick={handleConfirmPost}
              color="primary"
              variant="contained"
              size="small"
            >
              Confirmer
            </Button>
          </Box>
        </Box>
      </Popover>
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Supprimer les postes sélectionnés</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedIds.length} poste(s) seront définitivement supprimés de Firestore. Cette action
            est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Annuler
          </Button>
          <Button onClick={handleConfirmDeleteSelected} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Suppression…' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

